import { useState, useEffect } from "react";
import { List, X } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";

export default function Header({ t, lang, setLang }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { label: t.nav.services, href: "#services" },
    { label: t.nav.about, href: "#about" },
    { label: t.nav.whyUs, href: "#why-us" },
    { label: t.nav.process, href: "#process" },
    { label: t.nav.contact, href: "#contact" },
  ];

  const scrollTo = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#050505]/80 border-b border-white/10 shadow-lg"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <a
          href="#hero"
          onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          data-testid="logo-link"
          className="font-outfit font-bold text-xl md:text-2xl tracking-[0.15em] text-white hover:text-[#00F0FF] transition-colors"
        >
          ETRADE
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {navItems.map((item) => (
            <button
              key={item.href}
              onClick={() => scrollTo(item.href)}
              data-testid={`nav-${item.href.slice(1)}`}
              className="text-sm text-[#94A3B8] hover:text-[#00F0FF] transition-colors font-chivo tracking-wide"
            >
              {item.label}
            </button>
          ))}
          <div className="flex items-center gap-1 ml-4 text-sm font-chivo" data-testid="lang-switcher">
            <button
              onClick={() => setLang("en")}
              data-testid="lang-en"
              className={`px-2 py-1 rounded transition-colors ${
                lang === "en" ? "text-[#00F0FF] font-semibold" : "text-[#94A3B8] hover:text-white"
              }`}
            >
              EN
            </button>
            <span className="text-white/20">|</span>
            <button
              onClick={() => setLang("sl")}
              data-testid="lang-sl"
              className={`px-2 py-1 rounded transition-colors ${
                lang === "sl" ? "text-[#00F0FF] font-semibold" : "text-[#94A3B8] hover:text-white"
              }`}
            >
              SL
            </button>
          </div>
        </nav>

        {/* Mobile menu button */}
        <button
          className="md:hidden text-white p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X size={24} /> : <List size={24} />}
        </button>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#050505]/95 backdrop-blur-xl border-b border-white/10 overflow-hidden"
            data-testid="mobile-nav"
          >
            <div className="px-6 py-4 flex flex-col gap-4">
              {navItems.map((item) => (
                <button
                  key={item.href}
                  onClick={() => scrollTo(item.href)}
                  data-testid={`mobile-nav-${item.href.slice(1)}`}
                  className="text-left text-[#94A3B8] hover:text-[#00F0FF] transition-colors font-chivo"
                >
                  {item.label}
                </button>
              ))}
              <div className="flex items-center gap-2 pt-2 border-t border-white/10">
                <button
                  onClick={() => { setLang("en"); setMobileOpen(false); }}
                  data-testid="mobile-lang-en"
                  className={`px-3 py-1 rounded text-sm ${
                    lang === "en" ? "text-[#00F0FF] font-semibold" : "text-[#94A3B8]"
                  }`}
                >
                  EN
                </button>
                <button
                  onClick={() => { setLang("sl"); setMobileOpen(false); }}
                  data-testid="mobile-lang-sl"
                  className={`px-3 py-1 rounded text-sm ${
                    lang === "sl" ? "text-[#00F0FF] font-semibold" : "text-[#94A3B8]"
                  }`}
                >
                  SL
                </button>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
